import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { getImageSizeFromSteps } from "@features/images/utils";
import {
  isThereEmptyZones,
  renderImage,
} from "@features/studio/use-render-image";
import { useStudio } from "@features/studio/use-studio";
import { useEffectPlease } from "@features/utils";
import { useSavedState } from "@features/utils/use-saved-state";
import {
  Button,
  Card,
  Heading,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface StudioCropExtendProps {
  image: ImagesType;
}

export const StudioCropExtend = ({ image }: StudioCropExtendProps) => {
  const { t } = useTranslation();

  const formatCategories = {
    standard: {
      name: t("studio.crop_extend.formats.standard"),
      formats: [
        {
          id: "1x1",
          name: t("studio.crop_extend.formats.square_1_1"),
          width: 1024,
          height: 1024,
          ratio: "1:1",
        },
        {
          id: "5x3",
          name: t("studio.crop_extend.formats.landscape_5_3"),
          width: 1280,
          height: 768,
          ratio: "5:3",
        },
        {
          id: "3x5",
          name: t("studio.crop_extend.formats.portrait_3_5"),
          width: 768,
          height: 1280,
          ratio: "3:4",
        },
      ],
    },
    linkedin: {
      name: t("studio.crop_extend.formats.linkedin"),
      formats: [
        {
          id: "li-profile",
          name: t("studio.crop_extend.formats.profile"),
          width: 400,
          height: 400,
          ratio: "1:1",
        },
        {
          id: "li-banner",
          name: t("studio.crop_extend.formats.banner"),
          width: 1584,
          height: 396,
          ratio: "4:1",
        },
        {
          id: "li-post",
          name: t("studio.crop_extend.formats.post"),
          width: 1200,
          height: 627,
          ratio: "1.91:1",
        },
        {
          id: "li-cover",
          name: t("studio.crop_extend.formats.cover"),
          width: 1584,
          height: 396,
          ratio: "4:1",
        },
        {
          id: "li-article",
          name: t("studio.crop_extend.formats.post"),
          width: 1200,
          height: 627,
          ratio: "1.91:1",
        },
      ],
    },
    instagram: {
      name: t("studio.crop_extend.formats.instagram"),
      formats: [
        {
          id: "ig-square",
          name: t("studio.crop_extend.formats.square_post"),
          width: 1080,
          height: 1080,
          ratio: "1:1",
        },
        {
          id: "ig-portrait",
          name: t("studio.crop_extend.formats.portrait_post"),
          width: 1080,
          height: 1350,
          ratio: "4:5",
        },
        {
          id: "ig-story",
          name: t("studio.crop_extend.formats.story"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
        {
          id: "ig-reel",
          name: t("studio.crop_extend.formats.reel"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
        {
          id: "ig-igtv",
          name: t("studio.crop_extend.formats.igtv"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
      ],
    },
    facebook: {
      name: t("studio.crop_extend.formats.facebook"),
      formats: [
        {
          id: "fb-post",
          name: t("studio.crop_extend.formats.post"),
          width: 1200,
          height: 630,
          ratio: "1.91:1",
        },
        {
          id: "fb-cover",
          name: t("studio.crop_extend.formats.cover"),
          width: 820,
          height: 312,
          ratio: "2.63:1",
        },
        {
          id: "fb-story",
          name: t("studio.crop_extend.formats.story"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
        {
          id: "fb-event",
          name: t("studio.crop_extend.formats.event"),
          width: 1920,
          height: 1080,
          ratio: "16:9",
        },
      ],
    },
    twitter: {
      name: t("studio.crop_extend.formats.twitter"),
      formats: [
        {
          id: "tw-post",
          name: t("studio.crop_extend.formats.post"),
          width: 1200,
          height: 675,
          ratio: "16:9",
        },
        {
          id: "tw-header",
          name: t("studio.crop_extend.formats.header"),
          width: 1500,
          height: 500,
          ratio: "3:1",
        },
        {
          id: "tw-card",
          name: t("studio.crop_extend.formats.card"),
          width: 800,
          height: 418,
          ratio: "1.91:1",
        },
      ],
    },
    youtube: {
      name: t("studio.crop_extend.formats.youtube"),
      formats: [
        {
          id: "yt-thumbnail",
          name: t("studio.crop_extend.formats.thumbnail"),
          width: 1280,
          height: 720,
          ratio: "16:9",
        },
        {
          id: "yt-banner",
          name: t("studio.crop_extend.formats.banner"),
          width: 2560,
          height: 1440,
          ratio: "16:9",
        },
        {
          id: "yt-shorts",
          name: t("studio.crop_extend.formats.shorts"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
      ],
    },
    tiktok: {
      name: t("studio.crop_extend.formats.tiktok"),
      formats: [
        {
          id: "tt-video",
          name: t("studio.crop_extend.formats.video"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
        {
          id: "tt-profile",
          name: t("studio.crop_extend.formats.profile_photo"),
          width: 200,
          height: 200,
          ratio: "1:1",
        },
      ],
    },
    pinterest: {
      name: t("studio.crop_extend.formats.pinterest"),
      formats: [
        {
          id: "pt-pin",
          name: t("studio.crop_extend.formats.pin"),
          width: 1000,
          height: 1500,
          ratio: "2:3",
        },
        {
          id: "pt-square",
          name: t("studio.crop_extend.formats.square_pin"),
          width: 1000,
          height: 1000,
          ratio: "1:1",
        },
        {
          id: "pt-story",
          name: t("studio.crop_extend.formats.story_pin"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
      ],
    },
    onlyfans: {
      name: t("studio.crop_extend.formats.onlyfans"),
      formats: [
        {
          id: "of-post",
          name: t("studio.crop_extend.formats.post"),
          width: 1080,
          height: 1350,
          ratio: "4:5",
        },
        {
          id: "of-story",
          name: t("studio.crop_extend.formats.story"),
          width: 1080,
          height: 1920,
          ratio: "9:16",
        },
        {
          id: "of-profile",
          name: t("studio.crop_extend.formats.profile_photo"),
          width: 400,
          height: 400,
          ratio: "1:1",
        },
      ],
    },
    print: {
      name: t("studio.crop_extend.formats.print"),
      formats: [
        { id: "a4", name: "A4", width: 2480, height: 3508, ratio: "A4" },
        { id: "a3", name: "A3", width: 3508, height: 4960, ratio: "A3" },
        {
          id: "letter",
          name: "Letter US",
          width: 2550,
          height: 3300,
          ratio: "Letter",
        },
        {
          id: "postcard",
          name: t("studio.crop_extend.formats.postcard"),
          width: 1800,
          height: 1200,
          ratio: "3:2",
        },
      ],
    },
  };
  const { studio, setStudio } = useStudio();
  const [customWidth, setCustomWidth] = useState<number>(studio?.frame?.width);
  const [customHeight, setCustomHeight] = useState<number>(
    studio?.frame?.height
  );
  const [preferedId, setPreferedId] = useState<string | null>(null);

  const flatFormats = Object.values(formatCategories).flatMap((category) =>
    category.formats.map((format) => ({
      ...format,
      category: category.name,
    }))
  );

  const lastStepSize = getImageSizeFromSteps(
    image?.steps.filter((a) => a.mode !== "edits")
  );

  const handleFormatChange = useCallback(
    (formatId: string, forcePlacement?: boolean) => {
      if (formatId === "current") {
        setCustomWidth(lastStepSize.width);
        setCustomHeight(lastStepSize.height);
      }
      setPreferedId(formatId);

      const targetWidth =
        formatId === "current"
          ? lastStepSize.width
          : formatId === "custom"
          ? customWidth
          : flatFormats.find((f) => f.id === formatId)?.width || 1080;
      const targetHeight =
        formatId === "current"
          ? lastStepSize.height
          : formatId === "custom"
          ? customHeight
          : flatFormats.find((f) => f.id === formatId)?.height || 1080;

      // Compute autoplacement of the image (center and cover the frame)
      let scale = 1;
      if (
        lastStepSize.width / lastStepSize.height <
        targetWidth / targetHeight
      ) {
        scale = targetWidth / lastStepSize.width;
      } else {
        scale = targetHeight / lastStepSize.height;
      }

      setStudio({
        ...(studio.transform.changed && !forcePlacement
          ? {}
          : {
              transform: {
                x: 0,
                y: 0,
                rotation: 0,
                scaleX: scale || 1,
                scaleY: scale || 1,
                changed: false,
              },
            }),
        frame: {
          width: targetWidth,
          height: targetHeight,
        },
      });
    },
    [
      lastStepSize,
      customWidth,
      customHeight,
      flatFormats,
      studio.transform,
      setStudio,
    ]
  );

  useEffectPlease(() => {
    handleFormatChange("custom");
  }, [customWidth, customHeight]);

  const selectedFormatId =
    preferedId === "custom"
      ? "custom"
      : (flatFormats || []).find(
          (f) =>
            f.width === studio?.frame?.width &&
            f.height === studio?.frame?.height &&
            f.id === preferedId
        )?.id ||
        (flatFormats || []).find(
          (f) =>
            f.width === studio?.frame?.width &&
            f.height === studio?.frame?.height
        )?.id ||
        (customHeight === image.size.height && customWidth === image.size.width
          ? "current"
          : "custom");

  let selectedFormat = flatFormats.find((f) => f.id === selectedFormatId);

  return (
    <div className="space-y-8">
      <div>
        <Heading size="3">{t("studio.crop_extend.title")}</Heading>
        <Text size="2" color="gray" className="mt-1">
          {t("studio.crop_extend.description")}
        </Text>
      </div>

      <div className="space-y-4">
        {/* Format selector */}
        <div className="space-y-2">
          <Heading size="2">{t("studio.crop_extend.choose_format")}</Heading>

          <Text size="1" className="block">
            {t("studio.crop_extend.choose_format_description")}
          </Text>

          <ResponsiveSelect
            className="w-full"
            value={selectedFormatId}
            onChange={handleFormatChange}
            items={[
              {
                value: "current",
                label: t("studio.crop_extend.original_format"),
              },
              { value: "custom", label: t("studio.crop_extend.custom_format") },
              ...flatFormats.map((f) => ({
                value: f.id,
                label: `${f.name}`,
                group: f.category,
              })),
            ]}
            renderTrigger={() => (
              <>
                <span className="text-xs uppercase text-gray-500">
                  {flatFormats.find((f) => f.id === selectedFormatId)?.category
                    ? flatFormats.find((f) => f.id === selectedFormatId)
                        ?.category + " / "
                    : ""}
                </span>
                {flatFormats.find((f) => f.id === selectedFormatId)?.name ||
                  t("studio.crop_extend.custom_format_label")}
              </>
            )}
          />
        </div>

        {/* Custom dimensions input */}
        {(selectedFormatId === "custom" || selectedFormatId === "current") && (
          <Card>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Text size="1" color="gray">
                  {t("studio.crop_extend.width_label")}
                </Text>
                <TextField.Root
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Number(e.target.value))}
                  min="1"
                  max="10000"
                />
              </div>
              <div>
                <Text size="1" color="gray">
                  {t("studio.crop_extend.height_label")}
                </Text>
                <TextField.Root
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Number(e.target.value))}
                  min="1"
                  max="10000"
                />
              </div>
            </div>
            <Text size="1" color="gray" className="mt-2">
              {t("studio.crop_extend.ratio_label")}{" "}
              {genRatio(customWidth, customHeight)}
            </Text>
          </Card>
        )}

        {/* Selected format preview */}
        {selectedFormatId &&
          selectedFormatId !== "current" &&
          selectedFormatId !== "custom" && (
            <Card>
              <Text size="2">
                {t("studio.crop_extend.selected_format")}{" "}
                {selectedFormatId === "custom"
                  ? `${customWidth}×${customHeight} (${(
                      customWidth / customHeight
                    ).toFixed(2)}:1)`
                  : selectedFormat
                  ? `${selectedFormat.name} (${selectedFormat.width}×${selectedFormat.height})`
                  : selectedFormatId}
              </Text>
              {selectedFormat && (
                <Text size="1" color="gray" className="mt-2 block">
                  {t("studio.crop_extend.ratio_label")}{" "}
                  {genRatio(selectedFormat.width, selectedFormat.height)}
                </Text>
              )}
            </Card>
          )}

        {!!studio?.transform?.changed && (
          <Button
            onClick={() => {
              handleFormatChange(selectedFormatId, true);
            }}
            className="mt-4 w-full"
          >
            {t("studio.crop_extend.recenter_button")}
          </Button>
        )}
      </div>
      <ExtendPart image={image} />
    </div>
  );
};

export const ExtendPart = ({ image }: { image: ImagesType }) => {
  const { t } = useTranslation();
  const { studio } = useStudio();
  const { extend } = useImage(image.id);
  const [hasEmptyZones, setHasEmptyZones] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extendsCount, setExtendsCount] = useSavedState("extend-count", 2);
  const [prompt, setPrompt] = useState("");

  useEffectPlease(() => {
    (async () =>
      setHasEmptyZones(
        await isThereEmptyZones(image, studio?.frame, studio?.transform)
      ))();
  }, [
    image.url,
    JSON.stringify(studio?.frame),
    JSON.stringify(studio?.transform),
  ]);

  const handleExtend = useCallback(async () => {
    if (!hasEmptyZones || isProcessing) return;

    setIsProcessing(true);
    try {
      // Use the renderImage function to get the current view with all effects applied
      const imageData = await renderImage(image, studio, {
        format: "png",
        frame: true,
        effects: true,
        transform: true,
        ignore_edited_image: true,
      });

      // Call the extend API with the prompt
      await extend(imageData, prompt, extendsCount, [], undefined, [], true);
    } catch (error) {
      console.error("Failed to extend image:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [
    hasEmptyZones,
    isProcessing,
    image,
    studio,
    extend,
    prompt,
    extendsCount,
  ]);
  return (
    <div className="mt-4 space-y-2">
      <Heading size="2">{t("studio.crop_extend.extend_title")}</Heading>
      <Text size="1" className="block">
        {t("studio.crop_extend.extend_description")}
      </Text>

      <TextArea
        placeholder={
          t("studio.crop_extend.prompt_placeholder") ||
          "Describe what you want to extend the image with..."
        }
        value={prompt}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setPrompt(e.target.value)
        }
        size="2"
        rows={2}
        className="w-full"
      />

      <div className="flex space-x-2 w-full">
        <ResponsiveSelect
          size="2"
          value={extendsCount.toString()}
          onChange={(value) => setExtendsCount(Number(value))}
          items={[
            { value: "1", label: "1" },
            { value: "2", label: "2" },
            { value: "3", label: "3" },
            { value: "4", label: "4" },
          ]}
          renderTrigger={() => (
            <span className="text-xs uppercase text-gray-500">
              {extendsCount}
            </span>
          )}
        />
        <div className="grow">
          <UpgradeNeeded minimumCredits={extendsCount * 2}>
            <Button
              variant="solid"
              size="2"
              className="w-full"
              disabled={!hasEmptyZones || isProcessing}
              onClick={handleExtend}
            >
              <span>
                {isProcessing
                  ? t("studio.crop_extend.extending")
                  : t("studio.crop_extend.extend_button")}{" "}
                <CoinsVal val={extendsCount * 2} />
              </span>{" "}
            </Button>
          </UpgradeNeeded>
        </div>
      </div>
      {!hasEmptyZones && (
        <Text size="1" color="gray">
          {t("studio.crop_extend.no_empty_areas")}
        </Text>
      )}
    </div>
  );
};

const genRatio = (width: number, height: number) => {
  if (width === height) return "1:1";
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${width / divisor}:${height / divisor}`;
};
